/**
 * O cartão da Previsão de fechamento (história 11.3, FR-78) — Configuração → Negócios.
 *
 * A quinta lista do FR-67 mora no `negocios` (dono do dado) e tem forma própria: **rótulo**,
 * **cor** da paleta pastel fechada do `DESIGN.md` e **descrição** opcional. Sem data e sem
 * porcentagem. Numa instalação nova ela nasce com os três itens de fábrica; a escolha no Negócio e
 * o ponto no card são da 11.8 (`MarcaDePrevisao`), e o relatório por previsão é da 11.10.
 *
 * **A cor sai de uma lista fechada**, e não de um seletor livre (`DESIGN.md`, "Faça/Não faça"): o
 * `<select>` só oferece os oito nomes, e o ponto colorido vem **sempre** com o rótulo por extenso e
 * o nome da cor ao lado — cor sozinha não carrega significado (piso de acessibilidade).
 *
 * **Excluir é lógico e em dois passos**, no molde das listas de domínio: o item some das escolhas
 * novas e continua rotulando o Negócio que já o escolheu. Cadastrar de novo um rótulo excluído o
 * **reativa** (mesmo `id`), e a tela diz isso.
 *
 * **A edição só fecha no sucesso**: recusada (rótulo vazio aqui, `409`/`422` do serviço), ela fica
 * aberta com o que foi digitado; "Cancelar" descarta, e reabrir mostra os valores atuais.
 *
 * **Tudo o que o serviço recusar aparece verbatim** — o `403` de quem não tem
 * `administrar_listas_previsao_e_campos`, o `409` do rótulo repetido. Esconder botão por
 * permissão seria conveniência, e não autorização; quem recusa é o serviço.
 */

import { type FormEvent, useState } from 'react'

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import '@/modulos/cadastro/listas-de-dominio.css'
import {
  CORES_PASTEL,
  type CorPastel,
  NOMES_DAS_CORES,
  type PrevisaoDeFechamento as ItemDePrevisao,
  useCadastrarPrevisao,
  useEditarPrevisao,
  useExcluirPrevisao,
  usePrevisoes,
} from './api'
import { PontoDePrevisao } from './MarcaDePrevisao'
import './previsao-de-fechamento.css'

/** Os tetos do serviço (`TAMANHO_DO_ROTULO_DA_PREVISAO`, `TAMANHO_DA_DESCRICAO_DA_PREVISAO`). */
export const TAMANHO_DO_ROTULO_DA_PREVISAO = 80
export const TAMANHO_DA_DESCRICAO_DA_PREVISAO = 500

export const FALTA_ROTULO_DA_PREVISAO = 'Informe o rótulo do item.'
export const PREVISAO_CADASTRADA = 'Item cadastrado.'
export const PREVISAO_RESTAURADA = 'O item existia excluído e voltou para a lista.'
export const PREVISAO_SALVA = 'Item salvo.'
export const PREVISAO_EXCLUIDA = 'Item excluído. Os negócios que já o escolheram continuam com ele.'

const ID_DO_TITULO = 'previsao-de-fechamento-titulo'

function mensagemDe(falha: unknown): string {
  return falha instanceof ErroDaApi ? falha.message : MENSAGEM_DE_ERRO_DESCONHECIDO
}

/** O ponto de previsão (9 px) mora em `MarcaDePrevisao` desde a 11.8 — o card e o modal o usam. */
export { PontoDePrevisao }

function SeletorDeCor({
  id,
  valor,
  aoTrocar,
}: {
  id: string
  valor: CorPastel
  aoTrocar: (cor: CorPastel) => void
}) {
  return (
    <div className="listas__campo">
      <label className="listas__rotulo" htmlFor={id}>
        Cor
      </label>
      <select
        id={id}
        className="listas__entrada"
        value={valor}
        onChange={(evento) => {
          aoTrocar(evento.target.value as CorPastel)
        }}
      >
        {CORES_PASTEL.map((cor) => (
          <option key={cor} value={cor}>
            {NOMES_DAS_CORES[cor]}
          </option>
        ))}
      </select>
    </div>
  )
}

/** O que a linha pede ao cartão ao salvar: `aoConcluir` fecha a edição, e só no sucesso. */
interface EdicaoDaLinha {
  rotulo: string
  cor: CorPastel
  descricao: string
  aoConcluir: () => void
}

function LinhaDoItem({
  item,
  ocupado,
  aoSalvar,
  aoRecusar,
  aoExcluir,
}: {
  item: ItemDePrevisao
  ocupado: boolean
  aoSalvar: (item: ItemDePrevisao, edicao: EdicaoDaLinha) => void
  aoRecusar: (mensagem: string) => void
  aoExcluir: (item: ItemDePrevisao) => void
}) {
  const [editando, setEditando] = useState(false)
  const [confirmando, setConfirmando] = useState(false)
  const [rotulo, setRotulo] = useState(item.rotulo)
  const [cor, setCor] = useState<CorPastel>(item.cor)
  const [descricao, setDescricao] = useState(item.descricao ?? '')

  /** Abrir a edição **sempre** a partir dos valores atuais — o que ficou de um Cancelar some. */
  function abrirEdicao() {
    setRotulo(item.rotulo)
    setCor(item.cor)
    setDescricao(item.descricao ?? '')
    setEditando(true)
  }

  if (editando) {
    return (
      <li className="listas__item">
        <form
          className="listas__formulario previsao__formulario"
          onSubmit={(evento) => {
            evento.preventDefault()
            if (rotulo.trim() === '') {
              aoRecusar(FALTA_ROTULO_DA_PREVISAO)
              return
            }
            aoSalvar(item, {
              rotulo,
              cor,
              descricao,
              aoConcluir: () => {
                setEditando(false)
              },
            })
          }}
        >
          <div className="listas__campo">
            <label className="listas__rotulo" htmlFor={`previsao-rotulo-${item.id}`}>
              Rótulo
            </label>
            <input
              id={`previsao-rotulo-${item.id}`}
              className="listas__entrada"
              value={rotulo}
              maxLength={TAMANHO_DO_ROTULO_DA_PREVISAO}
              onChange={(evento) => {
                setRotulo(evento.target.value)
              }}
            />
          </div>
          <SeletorDeCor id={`previsao-cor-${item.id}`} valor={cor} aoTrocar={setCor} />
          <div className="listas__campo">
            <label className="listas__rotulo" htmlFor={`previsao-descricao-${item.id}`}>
              Descrição — opcional
            </label>
            <input
              id={`previsao-descricao-${item.id}`}
              className="listas__entrada"
              value={descricao}
              maxLength={TAMANHO_DA_DESCRICAO_DA_PREVISAO}
              onChange={(evento) => {
                setDescricao(evento.target.value)
              }}
            />
          </div>
          <div className="listas__acoes">
            <button type="submit" className="listas__botao" disabled={ocupado}>
              Salvar
            </button>
            <button
              type="button"
              className="listas__botao listas__botao--discreto"
              onClick={() => {
                setEditando(false)
              }}
            >
              Cancelar
            </button>
          </div>
        </form>
      </li>
    )
  }

  return (
    <li className="listas__item">
      <div className="listas__linha">
        <PontoDePrevisao cor={item.cor} />
        <span className="listas__valor">{item.rotulo}</span>
        <span className="listas__status">{NOMES_DAS_CORES[item.cor]}</span>
        <div className="listas__acoes">
          {confirmando ? (
            <>
              <button
                type="button"
                className="listas__botao listas__botao--perigo"
                disabled={ocupado}
                onClick={() => {
                  aoExcluir(item)
                  setConfirmando(false)
                }}
              >
                Confirmar exclusão
              </button>
              <button
                type="button"
                className="listas__botao listas__botao--discreto"
                onClick={() => {
                  setConfirmando(false)
                }}
              >
                Cancelar
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="listas__botao listas__botao--discreto"
                aria-label={`Editar ${item.rotulo}`}
                onClick={abrirEdicao}
              >
                Editar
              </button>
              <button
                type="button"
                className="listas__botao listas__botao--discreto"
                aria-label={`Excluir ${item.rotulo}`}
                onClick={() => {
                  setConfirmando(true)
                }}
              >
                Excluir
              </button>
            </>
          )}
        </div>
      </div>
      {item.descricao === null ? null : <p className="listas__ajuda">{item.descricao}</p>}
    </li>
  )
}

export function PrevisaoDeFechamento() {
  const consulta = usePrevisoes()
  const cadastro = useCadastrarPrevisao()
  const edicao = useEditarPrevisao()
  const exclusao = useExcluirPrevisao()

  const [rotulo, setRotulo] = useState('')
  const [cor, setCor] = useState<CorPastel>('salvia')
  const [descricao, setDescricao] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [confirmacao, setConfirmacao] = useState<string | null>(null)

  const ocupado = cadastro.isPending || edicao.isPending || exclusao.isPending

  function limpar() {
    setErro(null)
    setConfirmacao(null)
  }

  function aoFalhar(falha: unknown) {
    setConfirmacao(null)
    setErro(mensagemDe(falha))
  }

  function cadastrar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    limpar()
    if (rotulo.trim() === '') {
      setErro(FALTA_ROTULO_DA_PREVISAO)
      return
    }
    cadastro.mutate(
      { rotulo: rotulo.trim(), cor, descricao: descricao.trim() === '' ? null : descricao.trim() },
      {
        onSuccess: ({ restaurado }) => {
          setConfirmacao(restaurado ? PREVISAO_RESTAURADA : PREVISAO_CADASTRADA)
          setRotulo('')
          setDescricao('')
        },
        onError: aoFalhar,
      },
    )
  }

  return (
    <section className="listas" aria-labelledby={ID_DO_TITULO}>
      <div className="listas__cartao">
        <div className="listas__cabecalho">
          <h2 className="listas__titulo" id={ID_DO_TITULO}>
            Previsão de fechamento
          </h2>
          <span className="listas__ajuda">
            O vendedor escolhe um item (ou nenhum) em cada negócio. Cada item tem uma cor da paleta
            e uma descrição; não há data nem porcentagem.
          </span>
        </div>
        <div className="listas__corpo">
          {consulta.isError ? (
            <p className="listas__erro" role="alert">
              {mensagemDe(consulta.error)}
            </p>
          ) : consulta.isPending ? (
            <p className="listas__ajuda" role="status">
              Carregando a Previsão de fechamento…
            </p>
          ) : consulta.data.length === 0 ? (
            <p className="listas__ajuda">Nenhum item. Cadastre o primeiro abaixo.</p>
          ) : (
            <ul className="listas__lista" aria-label="Itens da Previsão de fechamento">
              {consulta.data.map((item) => (
                <LinhaDoItem
                  key={item.id}
                  item={item}
                  ocupado={ocupado}
                  aoRecusar={(mensagem) => {
                    setConfirmacao(null)
                    setErro(mensagem)
                  }}
                  aoSalvar={(atual, nova) => {
                    limpar()
                    edicao.mutate(
                      {
                        previsaoId: atual.id,
                        edicao: {
                          rotulo: nova.rotulo.trim(),
                          cor: nova.cor,
                          descricao: nova.descricao.trim() === '' ? null : nova.descricao.trim(),
                          ordem: atual.ordem,
                        },
                      },
                      {
                        onSuccess: () => {
                          nova.aoConcluir()
                          setConfirmacao(PREVISAO_SALVA)
                        },
                        onError: aoFalhar,
                      },
                    )
                  }}
                  aoExcluir={(atual) => {
                    limpar()
                    exclusao.mutate(atual.id, {
                      onSuccess: () => {
                        setConfirmacao(PREVISAO_EXCLUIDA)
                      },
                      onError: aoFalhar,
                    })
                  }}
                />
              ))}
            </ul>
          )}

          <form className="listas__formulario" onSubmit={cadastrar}>
            <div className="listas__campo">
              <label className="listas__rotulo" htmlFor="previsao-novo-rotulo">
                Novo item
              </label>
              <input
                id="previsao-novo-rotulo"
                className="listas__entrada"
                value={rotulo}
                maxLength={TAMANHO_DO_ROTULO_DA_PREVISAO}
                onChange={(evento) => {
                  setRotulo(evento.target.value)
                }}
              />
            </div>
            <SeletorDeCor id="previsao-nova-cor" valor={cor} aoTrocar={setCor} />
            <div className="listas__campo">
              <label className="listas__rotulo" htmlFor="previsao-nova-descricao">
                Descrição — opcional
              </label>
              <input
                id="previsao-nova-descricao"
                className="listas__entrada"
                value={descricao}
                maxLength={TAMANHO_DA_DESCRICAO_DA_PREVISAO}
                onChange={(evento) => {
                  setDescricao(evento.target.value)
                }}
              />
            </div>
            <div className="listas__acoes">
              <button type="submit" className="listas__botao" disabled={ocupado}>
                Cadastrar item
              </button>
            </div>
          </form>

          {erro === null ? null : (
            <p className="listas__erro" role="alert">
              {erro}
            </p>
          )}
          {confirmacao === null || erro !== null ? null : (
            <p className="listas__sucesso" role="status">
              {confirmacao}
            </p>
          )}
        </div>
      </div>
    </section>
  )
}
